import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { Icons } from '../../../shared/helpers/icons';
import { CommonModule } from '@angular/common';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { IContact, IUserDataInfo, IUserInfo } from '../../../shared/models/contact';
import { IDepartment } from '../../../shared/models/department';
import { ContactInfoPopupComponent } from '../../components/contact-info-popup/contact-info-popup.component';
import { FormsModule } from '@angular/forms'; 
import { ContactsService } from '../../../shared/services/contact.service';
import { UserDataService } from '../../../shared/services/userData.service';
import { CardsListSkeletonComponent } from "../../components/cards-list-skeleton/cards-list-skeleton.component";
import { DepartmentsService } from '../../../shared/services/department.service';
import { EopAssistantComponent } from "../../components/eop-assistant/eop-assistant.component";
import { PopupMessageComponent } from "../../components/popup-message/popup-message.component";
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PlanningTeamDetailsComponent } from "./planning-team-details/planning-team-details.component";
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { EEopTemplates, ESectionTypes, ESubSectionTypes } from '../../../shared/models/eop_types.enum';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'core-planning-team',
  standalone: true,
  imports: [
    LucideAngularModule,
    CommonModule,
    FormsModule,
    PrvNextButtonsComponent,
    ContactInfoPopupComponent,
    CardsListSkeletonComponent,
    EopAssistantComponent,
    PopupMessageComponent,
    IntroductionComponent,
    SpinnerComponent,
    PlanningTeamDetailsComponent,
    NoEmojiDirective
],
  templateUrl: './core-planning-team.component.html',
  styleUrl: './core-planning-team.component.css'
})
export class CorePlanningTeamComponent implements OnInit {

  currentRoute: string = 'core-planning-team';
  sectionType = ESectionTypes['Core Planning Team'];

  icons = Icons;
  orgType: string = '1'; // Default to '1' (K-12)
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  searchQuery: string = '';
  loadingContacts: boolean = true;
  contactInfo: IContact | null = null;
  selectedDepartmentId: string | null = null;
  messageModal: string | null = null;
  popupMessage: string = '';
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];
  departmentsList: IDepartment[] = [];
  selectedContacts: IContact[] = [];
  allPlanningTeam: any[] = [];

  loading: boolean = false;
  showPlanningTeamModal: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private departmentsService: DepartmentsService,
    public eopNavigationService: EopNavigationService
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.orgType = this.userDataService.getOrgType() || '1'; // Default to '1' if null
    this.loadContacts();
    this.loadDepartments();

    if (this.selectedContacts.length > 0) {
      this.selectedContacts.sort((a, b) => a.name.localeCompare(b.name));
    }
  };

  loadBuildingAllPlanningTeam(): void {
    if (this.currentBuildingId) {
      this.loading
      this.contactsService.getBuildingPlanningTeam(this.currentBuildingId, EEopTemplates["REMs EOP"]).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.allPlanningTeam = response.data || [];
            console.log('Successfully fetching all planning team:', response);
            if (this.contactsList.length > 0) {
              this.updateSelectedContacts(Number(this.selectedDepartmentId));
            }
          }
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error fetching all planning team:', error.message); 
          this.allPlanningTeam = [];
        }
      })
    }
  }

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loadingContacts = false;
        this.loading = false;
        this.fullContactsList = response.data || [];
        // Check if user is an nside.io user
        if (this.currentUser?.email?.toLowerCase().endsWith('@nside.io')) {
          // Show full list
          this.contactsList = [...this.fullContactsList];
        } else {
          // Show only non-nside.io contacts
          this.contactsList = this.fullContactsList.filter(
            (c: any) => !c.email?.toLowerCase().endsWith('@nside.io')
          );
        }
        console.log('Contacts loaded successfully:', response);
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
        }
        this.loadBuildingAllPlanningTeam();
      },
      error: (error: any) => {
        this.loading = false;
        this.loadingContacts = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  // Load Departments 
  loadDepartments(): void {
    this.loading = true;
    this.departmentsService.getDepartments(EEopTemplates['REMs EOP'], this.orgType).subscribe({
      next: (response) => {
        this.loading = false;
        this.departmentsList = response.data ? response.data : [];
        this.selectedDepartmentId = this.departmentsList.length > 0 ? String(this.departmentsList[0].id) : null;
      },
      error: (error: any) => {
        this.loading = false;
        console.error('Error fetching departments:', error.message); 
      },
    });
  };  

  filterContacts(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.contactsList = this.fullContactsList.filter((item) => {
      return (
        item.name.toLowerCase().includes(query) ||
        item.Title?.toLowerCase().includes(query) ||
        item.workphone?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query)
      );
    });
  }
  
  isContactSelected(contact: IContact): boolean {
    return this.selectedContacts.some(selectedContact => selectedContact.id === contact.id);
  }

  onDepartmentChange(event: any): void {
    this.selectedDepartmentId = event.target.value;
    this.updateSelectedContacts(Number(this.selectedDepartmentId));
  };

  updateSelectedContacts(departmentId: number): void {
    const departmentEntries = this.allPlanningTeam.filter(
      entry => entry.collaborative_department === departmentId
    );
  
    const contactIds = departmentEntries.map(entry => entry.contact_id);
  
    this.selectedContacts = this.contactsList
      .filter(contact => contactIds.includes(contact.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  handleOpenModal(modalName: string): void {
    if (modalName === 'planning-team') {
      this.showPlanningTeamModal = true;
    }
  }

  handleCloseModal(modalName: string): void {
    if (modalName === 'planning-team') {
      this.showPlanningTeamModal = false;
    } 
  }
  

  // open contact info pop up
  openInfo(contact: IContact): void {
    this.contactInfo = contact;
  };

  // add contact to the department
  addContactToDepartment(contact: IContact) {
    if (this.isContactSelected(contact)) {
      alert('This contact has been assigned to this department already.');
    } else {
      this.selectedContacts.push(contact);
      console.log(contact.id);
    }
  };

  // remove contact from department
  removeContactFromDepartment(selectedContact: IContact): void {
    const index = this.selectedContacts.findIndex(contact => contact.name === selectedContact.name);
    if (index !== -1) {
      this.selectedContacts.splice(index, 1);
    }
  };

  // save new changes in department.
  saveSelectedContacts(): void {
    if (this.currentBuildingId && this.selectedDepartmentId) {
      this.loading = true;
  
      const payload = this.selectedContacts.map(contact => ({
        collaborative_department: Number(this.selectedDepartmentId),
        contact_id: contact.id
      }));
  
      console.log(payload);
      if (payload.length === 0) {
        // If no contacts are selected, remove all contacts from the department\
        this.contactsService.removeDepartmentContacts(this.currentBuildingId, this.selectedDepartmentId).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              console.log('All contacts successfully removed from department:', response);
              this.messageModal = 'success';
              this.popupMessage = 'This occurred after saving collaborative planning team.';
              this.loadBuildingAllPlanningTeam();
            }
          },
          error: (error) => {
            this.loading = false;
            console.error('Error removing contacts from department:', error.message);
            this.messageModal = 'unsuccess';
            this.popupMessage = error.message;
          }
        })
      } else {
        this.contactsService.saveDepartmentContacts(
          this.currentBuildingId,
          payload
        ).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              console.log('Contacts successfully saved to department:', response);
              this.messageModal = 'success';
              this.popupMessage = 'This occurred after saving collaborative planning team.';
              this.loadBuildingAllPlanningTeam();
            }
          },
          error: (error) => {
            this.loading = false;
            console.error('Error saving contacts to department:', error.message);
            this.messageModal = 'unsuccess';
            this.popupMessage = error.message;
          }
        });
      }
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
  

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }
};
